import {
  analyticsErrorCode,
  isAnalyticsUrlProperty,
  isSensitiveAnalyticsProperty,
  redactAnalyticsText,
  sanitizeAnalyticsUrl
} from './analyticsPrivacy';

export const POSTHOG_SCHEMA_VERSION = 2;

const POSTHOG_DUPLICATE_STREAMS = new Set([
  'exception',
  'web_vitals',
  'session_summary'
]);

const POSTHOG_TECHNICAL_GA_PROPERTIES = new Set([
  'session_id',
  'engagement_time_msec',
  'event_id'
]);

const ERROR_VALUE_KEYS = new Set([
  'error',
  'error_message',
  'message',
  'reason',
  'failure_reason'
]);

const MASKED_TEXT_EVENT_NAMES = new Set([
  '$autocapture',
  '$rageclick',
  '$dead_click',
  '$dead_swipe'
]);

const getEnvironment = () => {
  const configured = String(process.env.REACT_APP_APP_ENV || '').trim().toLowerCase();
  if (configured) return configured.slice(0, 32);
  return process.env.NODE_ENV === 'production' ? 'production' : 'development';
};

const routeName = (properties = {}) => {
  const raw = properties.route_name || properties.app_route || properties.frontpage_route || properties.route || '';
  return String(raw || '')
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
};

const eventDomain = (eventName) => {
  const name = String(eventName || '').toLowerCase();
  if (/checkout|purchase|payment|subscription|refund|billing|coupon/.test(name)) return 'revenue';
  if (/login|logout|sign_up|signup|auth|magic_link|verification/.test(name)) return 'identity';
  if (/search|map|property|filter|polygon|marker|complex/.test(name)) return 'discovery';
  if (/report|export|stats|area_pulse|saved_area|llm|chat|ai_/.test(name)) return 'product';
  if (/exception|error|failed|web_vital|performance/.test(name)) return 'reliability';
  if (/page|frontpage|pricing|landing|lead|campaign/.test(name)) return 'acquisition';
  return 'engagement';
};

const eventOutcome = (eventName) => {
  const name = String(eventName || '').toLowerCase();
  if (/failed|failure|error|exception|rejected|denied/.test(name)) return 'failure';
  if (/completed|success|succeeded|created|activated|purchased|downloaded/.test(name)) return 'success';
  if (/started|submitted|initiated|selected|opened|viewed/.test(name)) return 'started';
  return undefined;
};

const countBucket = (value) => {
  const count = Number(value);
  if (!Number.isFinite(count) || count < 0) return undefined;
  if (count === 0) return '0';
  if (count <= 5) return '1_5';
  if (count <= 20) return '6_20';
  if (count <= 100) return '21_100';
  return '101_plus';
};

const durationBucket = (value) => {
  const duration = Number(value);
  if (!Number.isFinite(duration) || duration < 0) return undefined;
  if (duration < 500) return 'under_500ms';
  if (duration < 2000) return '500ms_2s';
  if (duration < 5000) return '2s_5s';
  if (duration < 15000) return '5s_15s';
  return '15s_plus';
};

const sanitizePostHogValue = (value, key, eventName, depth = 0) => {
  if (value === undefined || value === null || depth > 6) return undefined;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string') {
    if (isAnalyticsUrlProperty(key)) return sanitizeAnalyticsUrl(value);
    if (MASKED_TEXT_EVENT_NAMES.has(eventName) && /(^|\$|_)(text|el_text)(_|$)/i.test(key)) {
      return '[masked]';
    }
    return redactAnalyticsText(value, /stack/i.test(key) ? 2000 : 500);
  }
  if (Array.isArray(value)) {
    return value
      .slice(0, 50)
      .map((item) => sanitizePostHogValue(item, key, eventName, depth + 1))
      .filter((item) => item !== undefined);
  }
  if (typeof value === 'object') {
    const output = {};
    Object.entries(value).slice(0, 200).forEach(([nestedKey, nestedValue]) => {
      if (isSensitiveAnalyticsProperty(nestedKey)) return;
      const cleanValue = sanitizePostHogValue(nestedValue, nestedKey, eventName, depth + 1);
      if (cleanValue !== undefined && cleanValue !== '') output[nestedKey] = cleanValue;
    });
    return output;
  }
  return undefined;
};

const sanitizeCaptureProperties = (
  properties,
  eventName,
  isException = false,
  preservePostHogTransportProperties = false
) => {
  const output = {};
  Object.entries(properties || {}).slice(0, 200).forEach(([rawKey, rawValue]) => {
    const key = String(rawKey || '').trim();
    if (!key) return;
    // posthog-js injects this public project token after application capture
    // sanitization. before_send must preserve it or ingestion returns HTTP 401.
    if (preservePostHogTransportProperties && key === 'token') {
      output[key] = rawValue;
      return;
    }
    if (isSensitiveAnalyticsProperty(key)) return;
    if (POSTHOG_TECHNICAL_GA_PROPERTIES.has(key)) return;
    if (!isException && ERROR_VALUE_KEYS.has(key)) {
      output.error_code = analyticsErrorCode(rawValue);
      return;
    }
    const cleanValue = sanitizePostHogValue(rawValue, key, eventName, 0);
    if (cleanValue !== undefined && cleanValue !== '') output[key] = cleanValue;
  });
  return output;
};

export const preparePostHogCapture = (rawEventName, rawProperties = {}) => {
  const originalName = String(rawEventName || '').trim();
  if (!originalName || POSTHOG_DUPLICATE_STREAMS.has(originalName)) return null;

  const eventName = originalName === 'page_view'
    ? '$pageview'
    : (originalName === 'purchase' ? 'purchase_client_confirmed' : originalName);
  const properties = sanitizeCaptureProperties(rawProperties, eventName);
  const route = routeName(properties);
  const outcome = eventOutcome(eventName);
  const resultCount = properties.result_count ?? properties.results_count ?? properties.record_count;
  const elapsed = properties.duration_ms ?? properties.elapsed_ms ?? properties.search_duration_ms;

  const common = {
    analytics_schema_version: POSTHOG_SCHEMA_VERSION,
    app_name: 'casadata',
    app_surface: 'web',
    app_environment: getEnvironment(),
    event_source: 'client',
    event_domain: eventDomain(eventName),
    ...(route ? { route_name: route } : {}),
    ...(outcome ? { event_outcome: outcome } : {}),
    ...(countBucket(resultCount) ? { result_count_bucket: countBucket(resultCount) } : {}),
    ...(durationBucket(elapsed) ? { duration_bucket: durationBucket(elapsed) } : {}),
    ...(process.env.REACT_APP_VERSION ? { app_version: String(process.env.REACT_APP_VERSION).slice(0, 64) } : {})
  };

  if (eventName === '$pageview') {
    const browserUrl = typeof window !== 'undefined' ? window.location?.href : '';
    const browserPath = typeof window !== 'undefined' ? window.location?.pathname : '/';
    const currentUrl = sanitizeAnalyticsUrl(properties.page_location || browserUrl || '/');
    const pageProperties = { ...properties };
    ['page_location', 'page_path', 'page_title', 'page_referrer'].forEach((key) => {
      delete pageProperties[key];
    });
    return {
      event: eventName,
      properties: {
        ...pageProperties,
        ...common,
        $current_url: currentUrl,
        $pathname: String(properties.page_path || browserPath || '/').split(/[?#]/, 1)[0].slice(0, 200),
        $title: redactAnalyticsText(properties.page_title || '', 120),
        ...(properties.page_referrer ? { $referrer: sanitizeAnalyticsUrl(properties.page_referrer) } : {})
      }
    };
  }

  if (eventName === 'purchase_client_confirmed') {
    const transactionId = redactAnalyticsText(properties.transaction_id || '', 100);
    return {
      event: eventName,
      properties: {
        ...properties,
        ...common,
        authoritative: false,
        ...(transactionId ? { $insert_id: `client_purchase:${transactionId}` } : {})
      }
    };
  }

  return { event: eventName, properties: { ...properties, ...common } };
};

export const scrubPostHogEvent = (rawEvent) => {
  try {
    if (!rawEvent || typeof rawEvent !== 'object') return null;
    const eventName = String(rawEvent.event || '').trim();
    if (!eventName || POSTHOG_DUPLICATE_STREAMS.has(eventName)) return null;
    return {
      ...rawEvent,
      properties: sanitizeCaptureProperties(
        rawEvent.properties || {},
        eventName,
        eventName === '$exception',
        true
      )
    };
  } catch {
    return null;
  }
};

const posthogSchema = {
  POSTHOG_SCHEMA_VERSION,
  preparePostHogCapture,
  scrubPostHogEvent
};

export default posthogSchema;
